import { useEffect, useRef } from 'react';

/**
 * Calls `handler` when the Escape key is pressed.
 * Fires regardless of the active element — pressing Escape is a deliberate
 * "I want to leave" action even when focused in an input.
 *
 * Uses a ref so the listener always invokes the latest handler without
 * needing to re-register on every render (avoids stale-closure bugs).
 */
export function useEscapeKey(handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      // Skip if another handler already consumed this Escape press.
      // Radix Sheet/Dialog handles Escape in the capture phase, calls
      // preventDefault(), and unmounts the dialog before bubble-phase
      // listeners fire — so checking defaultPrevented is the most reliable
      // guard against double-handling.
      if (e.defaultPrevented) return;
      // Fallback: skip if a modal overlay is still in the DOM
      const openOverlay = document.querySelector(
        "[role='dialog'][data-state='open'], [role='alertdialog'][data-state='open']"
      );
      if (openOverlay != null) return;
      handlerRef.current();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
